import { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { sseHost } from '~constants/api';
import { RootState } from 'store';

interface SSEEvent {
  type: string;
  ref: string;
  ts: string;
}

interface UseSSEOptions {
  channels: string[];
  eventTypes: string[];
  onEvent: (event: SSEEvent) => void;
  enabled?: boolean;
}

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const BACKOFF_MULTIPLIER = 2;

const useSSE = ({ channels, eventTypes, onEvent, enabled = true }: UseSSEOptions) => {
  const sessionToken = useSelector((state: RootState) => state.main.login.sessionToken);
  const [connected, setConnected] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const onEventRef = useRef(onEvent);

  onEventRef.current = onEvent;

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setConnected(false);
  }, []);

  const channelsKey = channels.join(',');
  const eventTypesKey = eventTypes.join(',');

  useEffect(() => {
    if (!enabled || !sseHost || channels.length === 0 || eventTypes.length === 0 || !sessionToken) {
      cleanup();
      return;
    }

    const connect = () => {
      const channelParam = channels.join(',');
      // Token is passed as a query param because the native EventSource API doesn't support custom headers.
      // TAV-2278 spec preferred @microsoft/fetch-event-source to use an Authorization header instead — deferred to TAV-2426.
      const url = `${sseHost}/events?token=${encodeURIComponent(sessionToken)}&channels=${encodeURIComponent(channelParam)}`;

      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // System event: connection confirmed
      eventSource.addEventListener('connected', () => {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        setConnected(true);
      });

      // System event: server shutting down — close and immediately reconnect to a healthy pod
      eventSource.addEventListener('reconnect', () => {
        setConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
        reconnectTimeoutRef.current = setTimeout(connect, 0);
      });

      // System event: missed events on reconnect — trigger a refresh
      eventSource.addEventListener('catchup', (messageEvent) => {
        try {
          const data = JSON.parse(messageEvent.data) as { reason: string; channels: string[] };
          // Notify via onEvent so the component can refetch
          onEventRef.current({ type: 'catchup', ref: data.channels.join(','), ts: new Date().toISOString() });
        } catch {
          // Non-JSON catchup — ignore
        }
      });

      const systemEventTypes = new Set(['connected', 'reconnect', 'catchup']);

      // Register listeners for each requested event type
      for (const type of eventTypes) {
        if (systemEventTypes.has(type)) continue;

        eventSource.addEventListener(type, (messageEvent) => {
          try {
            const data = JSON.parse(messageEvent.data) as SSEEvent;
            onEventRef.current(data);
          } catch {
            // Non-JSON event — ignore
          }
        });
      }

      eventSource.onerror = () => {
        eventSource.close();
        eventSourceRef.current = null;
        setConnected(false);

        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * BACKOFF_MULTIPLIER, MAX_RECONNECT_DELAY_MS);

        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return cleanup;
  }, [enabled, channelsKey, eventTypesKey, cleanup, sessionToken]);

  return { connected };
};

export default useSSE;
